/**
 * AFDS 2.0 Collection Grid
 *
 * Filtering remains URL-driven so it works without JS and can be intercepted
 * by parent consumers such as Shop Car Care.
 *
 * This module provides progressive enhancement for:
 * - sorting
 * - grid-density controls
 * - Palo Alto native Quick View re-initialisation
 * - stable AFDS collection-grid lifecycle hooks
 */

(() => {
  const ROOT_SELECTOR = '[data-af-collection-grid]';
  const QUICK_VIEW_SELECTOR = '[data-button-quick-view]';

  function initThemeQuickView(root) {
    if (!root || !window.theme || typeof window.theme.initQuickView !== 'function') return;

    window.theme.initQuickView(root);
  }

  function initGrid(root) {
    if (!root) return;

    /*
     * Quick View must run even when the AFDS grid itself has already been
     * initialised. Shop Car Care can replace product-card markup inside an
     * existing grid root, creating fresh data-button-quick-view elements.
     *
     * Palo Alto guards its own triggers with data-initialized, so repeated
     * calls are safe and only bind newly injected buttons.
     */
    initThemeQuickView(root);

    if (root.dataset.afCollectionGridInitialised === 'true') return;

    const sort = root.querySelector('[data-af-collection-sort]');
    if (sort) {
      sort.addEventListener('change', () => {
        const form = sort.closest('[data-af-collection-sort-form]');
        if (form) form.submit();
      });
    }

    const view = root.querySelector('[data-af-grid-view]');
    const grid = root.querySelector('[data-af-collection-grid-loop]');

    if (view && grid) {
      const applyDensity = (density) => {
        const next = Math.max(1, Math.min(3, Number(density) || 2));
        view.dataset.viewDensity = String(next);

        view.querySelectorAll('.af-collection-grid__view-icon').forEach((icon, index) => {
          icon.classList.toggle('is-filled', index < next);
        });

        const mobileColumns = next === 1 ? 1 : 2;
        const tabletColumns = next + 1;
        const desktopColumns = next + 2;

        grid.style.setProperty('--grid-mobile', `repeat(${mobileColumns}, minmax(0, 1fr))`);
        grid.style.setProperty('--grid-tablet', `repeat(${tabletColumns}, minmax(0, 1fr))`);
        grid.style.setProperty('--grid', `repeat(${desktopColumns}, minmax(0, 1fr))`);
        grid.classList.toggle('hide-info', next === 3);

        try {
          window.sessionStorage.setItem('afCollectionGridDensity', String(next));
        } catch (error) {
          // Storage is an enhancement only.
        }
      };

      let initialDensity = Number(view.dataset.viewDensity) || 2;
      try {
        const storedDensity = Number(window.sessionStorage.getItem('afCollectionGridDensity'));
        if (storedDensity >= 1 && storedDensity <= 3) initialDensity = storedDensity;
      } catch (error) {
        // Ignore unavailable storage.
      }

      applyDensity(initialDensity);

      view.addEventListener('click', () => {
        const current = Number(view.dataset.viewDensity) || 2;
        applyDensity(current >= 3 ? 1 : current + 1);
      });
    }

    root.dataset.afCollectionGridInitialised = 'true';
    root.dispatchEvent(
      new CustomEvent('af:collection-grid:ready', {
        bubbles: true,
        detail: { root }
      })
    );
  }

  function init(scope = document) {
    if (!scope) return;

    if (scope.matches && scope.matches(ROOT_SELECTOR)) {
      initGrid(scope);
    }

    if (scope.querySelectorAll) {
      scope.querySelectorAll(ROOT_SELECTOR).forEach(initGrid);
    }
  }

  function refreshQuickViewFromNode(node) {
    if (!(node instanceof Element)) return;

    if (node.matches(ROOT_SELECTOR)) {
      initGrid(node);
      return;
    }

    const nestedRoots = node.querySelectorAll(ROOT_SELECTOR);
    if (nestedRoots.length) {
      nestedRoots.forEach(initGrid);
      return;
    }

    const containsQuickView =
      node.matches(QUICK_VIEW_SELECTOR) ||
      Boolean(node.querySelector(QUICK_VIEW_SELECTOR));

    if (!containsQuickView) return;

    const root = node.closest(ROOT_SELECTOR);
    if (root) initGrid(root);
  }

  function observeDynamicGridContent() {
    if (!document.body || typeof MutationObserver === 'undefined') return;

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach(refreshQuickViewFromNode);
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      init();
      observeDynamicGridContent();
    });
  } else {
    init();
    observeDynamicGridContent();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));

  document.addEventListener('af:collection-grid:refresh', (event) => {
    const root = event.detail && event.detail.root;
    if (root && root.matches && root.matches(ROOT_SELECTOR)) {
      initGrid(root);
    } else {
      init(event.target);
    }
  });
})();
